/**
 * The donation form.
 *
 * Talks to two REST routes — tr/v1/donation/payment-intent and
 * tr/v1/donation/subscription — both of which need the nonce localized into
 * TR_DONATION by inc/scripts.php. The old hardcoded
 * /wp-content/themes/true-report/theme/… paths are gone: they broke under a theme
 * rename, under a child theme, and (already) in wp-env, which is why several bugs in
 * this file went unseen for so long.
 */

/**
 * Stripe Elements appearance, shared by every element group.
 *
 * Hides the tab strip: each payment method gets its own mounted element inside its own
 * labelled card in step-2, so Stripe's own tabs would be a second, competing selector.
 */
const TR_STRIPE_APPEARANCE = {
	rules: {
		'.Tab--selected:focus': {
			outline: 'none',
			boxShadow: 'none',
		},
		'.Tab:focus': {
			outline: 'none',
			boxShadow: 'none',
		},
		'.Tab--selected': {
			backgroundColor: 'transparent',
			border: '0',
			boxShadow: 'none',
		},
		'.Tab': {
			margin: '-9999px',
			backgroundColor: 'transparent',
			border: '0',
		},
		'.TabLabel': {
			color: 'transparent',
		},
		'.TabIcon': {
			fill: 'transparent',
		},
	},
};

/** Query parameters the form consumes from a deep link, and only these are stripped. */
const TR_DONATION_PARAMS = [
	'is_custom_price',
	'price',
	'price_interval',
	'plan_name',
	'price_id',
	'paypal_plan_id',
];

document.addEventListener('alpine:init', () => {
	Alpine.data('donation', () => ({
		stripe: undefined,
		stripeCardElements: undefined,
		stripeGooglePayElements: undefined,
		stripeApplePayElements: undefined,
		priceInterval: 'one-time',
		productName: '',
		stripePriceId: '',
		paypalPlanId: '',
		price: 0,
		isCustomPrice: false,
		step: 1,
		email: '',
		surname: '',
		name: '',
		payment: '',
		loading: false,
		hasGooglePay: false,
		hasApplePay: false,
		errorMessage: '',
		thankYouPage: '',
		// Set once a Stripe intent exists for the current amount/plan. Its presence is
		// what stops step 1 ↔ step 2 round trips from minting a second customer and
		// mounting a second Payment Element into an already-occupied container.
		clientSecret: '',
		// Opaque handle to the Stripe objects this checkout owns. Replaces the customer
		// id the old flow handed to the browser and then accepted back, unauthenticated,
		// from anyone who had one.
		donorToken: '',
		walletsChecked: false,

		// using gtranslate
		getLocaleFromCookie: function () {
			const cookieValue = document.cookie
				.split('; ')
				.find((row) => row.startsWith('googtrans='));

			if (!cookieValue) {
				return 'zh-HK'; // default fallback
			}

			// Extract the target language (second part after split)
			// Format: /zh-CN/en or /zh-CN/zh-TW
			// slice/join rather than [1]: a cookie value containing '=' would otherwise
			// be truncated at the first one.
			const parts = cookieValue.split('=').slice(1).join('=').split('/');
			const targetLang = parts[2]; // gets 'en', 'zh-TW', 'ko', etc.

			// Map GTranslate language codes to Stripe locale codes
			const localeMap = {
				en: 'en',
				'zh-CN': 'zh',
				'zh-TW': 'zh-TW',
				'zh-HK': 'zh-HK',
				ko: 'ko',
				ja: 'ja',
				es: 'es',
				fr: 'fr',
				de: 'de',
				it: 'it',
				pt: 'pt',
			};

			return localeMap[targetLang] || 'zh-HK'; // fallback to zh-HK if not found
		},

		init: function () {
			this.thankYouPage = this.$root.dataset.thankYouPage ?? '';
			// TR_DONATION is localized from inc/scripts.php.
			this.stripe = Stripe(TR_DONATION.PUBLISHABLE_KEY);

			const url = new URL(window.location.href);
			const params = url.searchParams;

			// /support-us/?is_custom_price=0&price_interval=month&price=500&price_id=price_…&paypal_plan_id=P-…
			this.isCustomPrice = params.get('is_custom_price') === '1';
			this.price = params.get('price') || '';

			if (this.isCustomPrice) {
				this.priceInterval = params.get('price_interval') || '';
				this.step = 2;
			} else if (this.price) {
				this.priceInterval = params.get('price_interval') || '';
				this.productName = params.get('plan_name') || '';
				this.stripePriceId = params.get('price_id') || '';
				this.paypalPlanId = params.get('paypal_plan_id') || '';
				this.step = 2;
			}

			// Strip only the parameters above. Replacing the whole search string, as this
			// used to, also discarded utm_* / fbclid / referral tags, so campaign
			// attribution for donation drives was being thrown away.
			let changed = false;
			TR_DONATION_PARAMS.forEach((key) => {
				if (params.has(key)) {
					params.delete(key);
					changed = true;
				}
			});
			if (changed) {
				window.history.replaceState(
					null,
					'',
					url.pathname + url.search
				);
			}

			// A donation is only ever confirmed against the intent that was created for
			// it. If the donor goes back and picks a different amount, interval or plan,
			// the one we are holding no longer describes what they want, so it is
			// discarded and the next confirmPlan() builds a fresh one.
			['price', 'priceInterval', 'stripePriceId'].forEach((prop) => {
				this.$watch(prop, () => this.resetStripeForm());
			});

			// Landing straight on step 2 from a deep link needs the same setup the step-1
			// button does. Without this nobody arriving through the footer donation widget
			// (js/media-player.js links to /support-us?is_custom_price=…&price=…) ever got
			// a payment form — the intent was never created, so the card container sat on
			// its 載入中... placeholder indefinitely.
			if (this.step === 2) {
				this.confirmPlan();
			}
		},

		/** Numeric donation amount, whatever the input handed us. */
		amount: function () {
			const value = parseFloat(this.price);
			return Number.isFinite(value) ? value : 0;
		},

		/** One id per attempt, so a retried fetch is idempotent but a second genuine
		 * donation is not folded into the first. */
		newRequestId: function () {
			if (window.crypto?.randomUUID) {
				return window.crypto.randomUUID();
			}
			return (
				'r' +
				Math.random().toString(36).slice(2) +
				Date.now().toString(36)
			);
		},

		/**
		 * POST to a donation REST route.
		 *
		 * Returns the decoded body, or null. Every failure path is handled here: the old
		 * code awaited response.json() unguarded, so an endpoint returning a fatal's HTML
		 * body rejected the promise with nothing to catch it, and the donor was left
		 * looking at the 載入中... placeholder indefinitely.
		 */
		postJson: async function (route, body, { quiet = false } = {}) {
			try {
				const response = await fetch(`${TR_DONATION.REST}${route}`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': TR_DONATION.NONCE,
					},
					body: JSON.stringify(body),
				});

				const json = await response.json().catch(() => null);

				if (!response.ok) {
					if (!quiet) {
						this.errorMessage =
							json?.message || TR_DONATION.I18N.genericError;
					}
					return null;
				}

				return json;
			} catch (e) {
				if (!quiet) {
					this.errorMessage = TR_DONATION.I18N.networkError;
				}
				return null;
			}
		},

		/**
		 * Put the donor's name and email on the Stripe objects this checkout owns.
		 *
		 * Quiet: a failure here must not put an error in front of a donor whose payment
		 * is about to go through perfectly well.
		 */
		attachDonor: async function () {
			if (!this.donorToken) {
				return;
			}

			await this.postJson(
				'donation/donor',
				{
					donor_token: this.donorToken,
					email: this.email,
					name: this.name,
					surname: this.surname,
				},
				{ quiet: true }
			);
		},

		handleSubmit: async function () {
			if (this.loading) {
				return;
			}

			this.loading = true;
			this.errorMessage = '';

			try {
				// Best effort, and deliberately not gated on. The old flow aborted the
				// payment when this call failed — refusing a donor who was ready to give
				// because a display name could not be saved — and, because the early
				// return skipped the loading reset, left the button stuck for good.
				await this.attachDonor();

				const elements =
					this.payment === 'card'
						? this.stripeCardElements
						: this.payment === 'google_pay'
							? this.stripeGooglePayElements
							: undefined;

				if (!elements) {
					this.errorMessage = TR_DONATION.I18N.genericError;
					return;
				}

				const { error: submitError } = await elements.submit();
				if (submitError) {
					// Previously alerted and then fell straight through to
					// confirmPayment with an unvalidated element.
					this.errorMessage =
						submitError.message || TR_DONATION.I18N.genericError;
					return;
				}

				const { error } = await this.stripe.confirmPayment({
					elements,
					confirmParams: {
						return_url: this.thankYouPage,
					},
				});

				// On success the browser has already left for return_url, so anything
				// here is a failure. This result used to be destructured and never read,
				// which is why a declined card produced no feedback at all.
				if (error) {
					this.errorMessage =
						error.message || TR_DONATION.I18N.genericError;
				}
			} catch (e) {
				this.errorMessage = TR_DONATION.I18N.genericError;
			} finally {
				// In a finally, so an early return can never strand the button in its
				// loading state and force the donor to reload and start over.
				this.loading = false;
			}
		},

		decideStripeForm: async function () {
			// Going back to step 1 and forward again used to create a second Stripe
			// customer and a second PaymentIntent, then try to mount a second Payment
			// Element into a container that already held one — which Stripe refuses,
			// leaving the donor stuck on the loading placeholder and unable to pay
			// without a full reload.
			//
			// Alpine tears down the step-2 template on the way out, so the elements do
			// have to be built again; what must not happen is another round trip to
			// Stripe. An unchanged amount reuses the secret it already has. A changed one
			// clears it (see the watchers in init) and starts over.
			if (this.clientSecret) {
				await this.mountPaymentForms(this.clientSecret);
				return;
			}

			if (this.amount() > 0 && this.priceInterval === 'one-time') {
				await this.renderStripeOneTimeForm();
			} else if (this.stripePriceId) {
				await this.renderStripeSubscriptionForm();
			}
		},

		/** Discard the current intent so the next confirmPlan() builds a fresh one. */
		resetStripeForm: function () {
			this.clientSecret = '';
			this.donorToken = '';
			this.unmountElements();
		},

		unmountElements: function () {
			[
				this.stripeCardElements,
				this.stripeGooglePayElements,
				this.stripeApplePayElements,
			].forEach((group) => {
				try {
					group?.getElement('payment')?.unmount();
				} catch (e) {
					/* nothing mounted */
				}
			});

			this.stripeCardElements = undefined;
			this.stripeGooglePayElements = undefined;
			this.stripeApplePayElements = undefined;
		},

		renderStripeOneTimeForm: async function () {
			const json = await this.postJson('donation/payment-intent', {
				request_id: this.newRequestId(),
				// amount in cents; rounded here as well as server-side so a custom
				// price like 10.005 cannot send a fractional cent.
				amount: Math.round(this.amount() * 100),
				email: this.email,
				name: this.name,
				surname: this.surname,
			});

			if (!json?.client_secret) {
				return;
			}

			this.clientSecret = json.client_secret;
			this.donorToken = json.donor_token ?? '';
			await this.mountPaymentForms(json.client_secret);
		},

		renderStripeSubscriptionForm: async function () {
			const json = await this.postJson('donation/subscription', {
				request_id: this.newRequestId(),
				price_id: this.stripePriceId,
				email: this.email,
				name: this.name,
				surname: this.surname,
			});

			if (!json?.client_secret) {
				return;
			}

			this.clientSecret = json.client_secret;
			this.donorToken = json.donor_token ?? '';
			await this.mountPaymentForms(json.client_secret);
		},

		/**
		 * Mount the card and wallet elements.
		 *
		 * Deferred to $nextTick: the containers only exist once Alpine has rendered the
		 * x-if="step === 2" template. It used to work by accident, because the fetch
		 * above happened to give Alpine time to render first.
		 */
		mountPaymentForms: async function (clientSecret) {
			await this.canMakePayment();

			this.$nextTick(() => {
				this.unmountElements();
				this.renderCardForm(clientSecret);
				this.renderGooglePayForm(clientSecret);
			});
		},

		canMakePayment: async function () {
			// Only worth asking the browser once per page load; the answer is a device
			// capability, not a property of this donation.
			if (this.walletsChecked) {
				return;
			}
			this.walletsChecked = true;

			try {
				const paymentRequest = this.stripe.paymentRequest({
					country: 'HK',
					currency: 'hkd',
					total: {
						label: TR_DONATION.I18N.donationLabel,
						// Probe only — nothing downstream reads this figure, but it now
						// at least reflects the donation instead of a fixed 1000.
						amount: Math.max(1, Math.round(this.amount() * 100)),
					},
				});

				const data = await paymentRequest.canMakePayment();
				this.hasGooglePay = !!data?.googlePay;
				this.hasApplePay = !!data?.applePay;
			} catch (e) {
				this.hasGooglePay = false;
				this.hasApplePay = false;
			}
		},

		renderCardForm: function (clientSecret) {
			this.stripeCardElements = this.stripe.elements({
				clientSecret,
				appearance: TR_STRIPE_APPEARANCE,
				locale: this.getLocaleFromCookie(),
			});

			this.stripeCardElements
				.create('payment', {
					layout: 'tabs',
					defaultCollapsed: false,
					radio: true,
					spaceAccordionItems: true,
					wallets: { googlePay: 'never', applePay: 'never' },
					paymentMethodOrder: ['card'],
				})
				.mount('#stripe-card-payment-element');
		},

		// 20251018: Apple Pay is disabled — the mount below stays commented out. Kept
		// because re-enabling it is a one-line change; it now gates on hasApplePay and
		// keeps its own element group, rather than gating on hasGooglePay and writing
		// over the Google Pay handle as it used to.
		renderApplePayForm: function (clientSecret) {
			if (!this.hasApplePay) {
				return;
			}

			this.stripeApplePayElements = this.stripe.elements({
				clientSecret,
				locale: this.getLocaleFromCookie(),
				appearance: TR_STRIPE_APPEARANCE,
			});

			this.stripeApplePayElements.create('payment', {
				layout: 'tabs',
				defaultCollapsed: false,
				radio: true,
				spaceAccordionItems: true,
				wallets: { googlePay: 'never', applePay: 'auto' },
				paymentMethodOrder: ['apple_pay'],
			});
			// .mount('#stripe-apple-pay-payment-element');
		},

		renderGooglePayForm: function (clientSecret) {
			if (!this.hasGooglePay) {
				return;
			}

			this.stripeGooglePayElements = this.stripe.elements({
				clientSecret,
				locale: this.getLocaleFromCookie(),
				appearance: TR_STRIPE_APPEARANCE,
			});

			this.stripeGooglePayElements
				.create('payment', {
					layout: 'tabs',
					defaultCollapsed: false,
					radio: true,
					spaceAccordionItems: true,
					wallets: { googlePay: 'auto', applePay: 'never' },
					paymentMethodOrder: ['google_pay'],
				})
				.mount('#stripe-google-pay-payment-element');
		},

		confirmPlan: function () {
			this.errorMessage = '';
			this.decideStripeForm();
		},

		renderPaypalOneTimeButton: function () {
			// clear container;
			document.querySelector('#paypal-button-container').textContent = '';
			paypal_one_time
				.Buttons({
					style: {
						layout: 'horizontal',
						tagline: false,
						disableMaxWidth: true,
					},
					createOrder: (data, actions) => {
						return actions.order.create({
							intent: 'CAPTURE',
							purchase_units: [
								{
									amount: {
										currency_code: 'HKD',
										value: this.amount().toString(),
									},
								},
							],
							payment_source: {
								paypal: {
									experience_context: {
										shipping_preference: 'NO_SHIPPING',
									},
									email_address: this.email,
									name: {
										given_name: this.name,
										surname: this.surname,
									},
								},
							},
						});
					},
					onApprove: (data, actions) => {
						return actions.order.capture().then(() => {
							// Only the order id travels. Spreading `data` also put
							// payerID, billingToken and a live facilitatorAccessToken
							// into the URL — and so into browser history, the access
							// log, and the Referer sent to every third-party origin the
							// thank-you page touches. The server looks the rest up from
							// PayPal.
							this.goToThankYou({ orderID: data.orderID });
						});
					},
					onError: () => {
						this.errorMessage = TR_DONATION.I18N.genericError;
					},
				})
				.render('#paypal-button-container');
		},

		renderPaypalSubscriptionButton: function () {
			// clear container;
			document.querySelector('#paypal-button-container').textContent = '';

			paypal_subscription
				.Buttons({
					style: {
						layout: 'horizontal',
						tagline: false,
						disableMaxWidth: true,
					},
					createSubscription: (data, actions) => {
						return actions.subscription.create({
							plan_id: this.paypalPlanId,
							application_context: {
								shipping_preference: 'NO_SHIPPING',
							},
							payment_source: {
								paypal: {
									experience_context: {
										shipping_preference: 'NO_SHIPPING',
									},
									email_address: this.email,
									name: {
										given_name: this.name,
										surname: this.surname,
									},
								},
							},
						});
					},
					// A subscription button's `actions` exposes no `order`, so the old
					// `actions.order.capture()` threw TypeError, the redirect never
					// happened, and the donor was left on the form with a console error
					// — already being billed monthly, with no confirmation page and no
					// newsletter signup.
					onApprove: (data) => {
						this.goToThankYou({
							subscriptionID: data.subscriptionID,
						});
					},
					onError: () => {
						this.errorMessage = TR_DONATION.I18N.genericError;
					},
				})
				.render('#paypal-button-container');
		},

		goToThankYou: function (params) {
			const query = new URLSearchParams({
				...params,
				paymentSource: 'paypal',
			}).toString();
			window.location = this.thankYouPage + '?' + query;
		},

		decidePaypalButton: function () {
			if (this.amount() > 0 && this.priceInterval === 'one-time') {
				this.renderPaypalOneTimeButton();
			} else if (this.paypalPlanId) {
				this.renderPaypalSubscriptionButton();
			}
		},
	}));
});
